/**
 * Contratto condiviso fra server, front-end e (per documentazione) firmware.
 *
 * Regola generale del sistema: i controller inviano SOLO valori raw. Ogni
 * conversione in unita' fisiche e ogni decisione di irrigazione avvengono qui
 * lato server. Il firmware non conosce percentuali ne' soglie calibrate.
 */
// ---------------------------------------------------------------------------
// Dispositivi
// ---------------------------------------------------------------------------
export const DEVICE_TYPES = ['standalone', 'lora'];
export const IRRIGATION_MODES = ['off', 'interval', 'schedule'];
export const SENSORS = ['battery', 'moisture', 'water_level'];
/** Sensori effettivamente presenti su ciascuna tipologia di irrigatore. */
export const SENSORS_BY_TYPE = {
    standalone: ['battery', 'moisture', 'water_level'],
    lora: ['battery', 'moisture'],
};
// ---------------------------------------------------------------------------
// Calibrazione
// ---------------------------------------------------------------------------
export const CALIBRATION_KINDS = [
    'lipo_divider',
    'lipo_mv',
    'linear_map',
    'pressure_xgzp',
];
/** Curva di scarica LiPo 1S, ripresa dal gateway BLE originale. */
export const DEFAULT_LIPO_CURVE = [
    { mv: 3200, pct: 0 },
    { mv: 3400, pct: 5 },
    { mv: 3600, pct: 25 },
    { mv: 3750, pct: 50 },
    { mv: 3900, pct: 75 },
    { mv: 4150, pct: 100 },
];
export const DEFAULT_CALIBRATIONS = {
    standalone: {
        battery: {
            kind: 'lipo_divider',
            // Partitore 10k verso V+ / 30k verso massa, ADC 12 bit con riferimento 3.3 V.
            params: {
                r1_kohm: 10,
                r2_kohm: 30,
                vref: 3.3,
                adc_max: 4095,
                gain: 1,
                offset_mv: 0,
            },
        },
        moisture: {
            kind: 'linear_map',
            params: { raw_dry: 2500, raw_wet: 1100 },
        },
        water_level: {
            kind: 'pressure_xgzp',
            params: {
                k_factor: 4096,
                raw_offset: 0,
                tank_height_cm: 50,
                density: 1,
            },
        },
    },
    lora: {
        battery: {
            kind: 'lipo_mv',
            params: { curve: DEFAULT_LIPO_CURVE },
        },
        moisture: {
            kind: 'linear_map',
            // ADC CubeCell: fondo scala e partizionamento diversi dall'ESP32.
            params: { raw_dry: 2200, raw_wet: 950 },
        },
    },
};
export const DEFAULT_DEVICE_CONFIG = {
    mode: 'off',
    intervalMinutes: 60,
    intervalDurationMinutes: 2,
    reportIntervalMinutes: 30,
    moistureThresholdPct: 40,
    waterLevelMinPct: 10,
    maxDurationMinutes: 30,
    valveInvert: false,
};
/**
 * Limiti del nome di un irrigatore. Il minimo non e' pedanteria: il nome
 * identifica il dispositivo in dashboard ed e' quello che si digita per
 * confermarne l'eliminazione, quindi vuoto lo renderebbe inservibile.
 */
export const DEVICE_NAME_MIN_LENGTH = 1;
export const DEVICE_NAME_MAX_LENGTH = 60;
/** Le note sono libere e possono mancare: serve solo un tetto alla lunghezza. */
export const DEVICE_NOTES_MAX_LENGTH = 500;
export const CONFIG_LIMITS = {
    intervalMinutes: { min: 1, max: 1440 },
    intervalDurationMinutes: { min: 1, max: 120 },
    reportIntervalMinutes: { min: 1, max: 1440 },
    moistureThresholdPct: { min: 0, max: 100 },
    waterLevelMinPct: { min: 0, max: 100 },
    maxDurationMinutes: { min: 1, max: 120 },
    scheduleDurationMinutes: { min: 1, max: 120 },
};
export const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
export const ALL_DAYS = 0b1111111;
/** Converte il giorno di `Date.getDay()` (0 = domenica) nel bit corrispondente. */
export function jsDayToBit(jsDay) {
    return (jsDay + 6) % 7;
}
export function isDayEnabled(mask, jsDay) {
    return (mask & (1 << jsDayToBit(jsDay))) !== 0;
}
export const IRRIGATION_TRIGGERS = ['manual', 'interval', 'schedule'];
export const IRRIGATION_OUTCOMES = [
    'running',
    'completed',
    'aborted_moisture',
    'aborted_water_level',
    'aborted_command',
    'failed',
];
// ---------------------------------------------------------------------------
// Coda comandi
// ---------------------------------------------------------------------------
export const COMMAND_TYPES = ['irrigate', 'stop'];
export const COMMAND_STATES = ['pending', 'sent', 'acked', 'expired', 'failed'];
/** Un comando non consegnato entro questo tempo viene scartato invece che eseguito in ritardo. */
export const COMMAND_TTL_MINUTES = 120;
export const HISTORY_METRICS = [
    'batteryPct',
    'moisturePct',
    'waterLevelPct',
    'irrigationMinutes',
];
/** Oltre questo silenzio un dispositivo viene mostrato offline, in multipli del suo intervallo di report. */
export const OFFLINE_MISSED_REPORTS = 3;
/** Limite inferiore della finestra di offline, per non lampeggiare con intervalli molto brevi. */
export const OFFLINE_MIN_MINUTES = 15;
// ---------------------------------------------------------------------------
// Protocollo radio LoRa v2 (documentato qui, implementato nei due sketch)
// ---------------------------------------------------------------------------
export const LORA_PROTOCOL = {
    magic: 0xa6,
    version: 2,
    packetSize: 22,
    frequencyHz: 869_525_000,
    spreadingFactor: 8,
    bandwidthKhz: 125,
    codingRate: 5,
    /** Asimmetrica: il gateway va a rete, il nodo a batteria. */
    txPowerGatewayDbm: 20,
    txPowerNodeDbm: 17,
    preambleNormal: 8,
    /**
     * Preambolo lungo per bucare il duty-cycle RX del nodo. Deve contenere una
     * finestra di ascolto intera: 220 simboli a SF8 sono 450 ms, contro i 399
     * richiesti dal ciclo di 375 ms + finestra di 24. Il vincolo e' verificato
     * alla compilazione nei due protocol_v2.h del firmware, che restano la
     * definizione autoritativa: queste costanti sono qui per il front-end e la
     * documentazione.
     */
    preamblePush: 220,
    rxDutyCycleRxMs: 24,
    rxDutyCycleMs: 375,
};
export const LORA_MSG = {
    REQ: 1,
    PLAN: 2,
    OPEN_NOW: 3,
    CLOSE_NOW: 4,
    ACK: 5,
};
// ---------------------------------------------------------------------------
// Utilita' comuni
// ---------------------------------------------------------------------------
export function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
/** Normalizza un MAC in minuscolo senza separatori, cosi' formati diversi coincidono. */
export function normalizeHardwareCode(code) {
    return code.trim().toLowerCase().replace(/[^0-9a-f]/g, '');
}
export * from './calibration.js';
//# sourceMappingURL=index.js.map